import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import RunEventForm from './run-event-form';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { RunEvent } from '@/models/run-event.model';
import { getRunEvent } from '@/services/run-event.service';
import RunEventDistanceList from '../distance/run-event-distance-list';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="run-event-form">Run Event Detail</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="run-event-distances">Distances</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;

  const [activeTab, setActiveTab] = useState('run-event-form');
  const [formObject, setFormObject] = useState<RunEvent|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<RunEvent> = await getRunEvent(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching RunEvent: ${errorModel?.errorMessage}`);
        nav('run-events');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Event: ${formObject.name}` : 'New Run Event'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='run-events'> Run Events</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'run-event-form' && <RunEventForm formObject={formObject} pageState={pageState}/>}
          {activeTab == 'run-event-distances' && id && <RunEventDistanceList runEvent={formObject}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
